import find from "lodash/find";
import { Configuration } from "../../types/customization/configuration";
import { Content } from "../../types/customization/content";
import { ContentCondition, Template, TemplateCondition } from "../../types/customization/template";
import { findOptionDefinition } from "../../utils/customization/finders";

export function evalCondition(
  conditions: TemplateCondition[],
  template: Template,
  configuration: Configuration | undefined
): boolean {
  let conditionResult = true;

  for (const condition of conditions) {
    const { optionDefinitionKey } = condition;
    const optionDefinition = findOptionDefinition(optionDefinitionKey, template);

    switch (optionDefinition.type) {
      case "customization": {
        if (!configuration) {
          conditionResult = conditionResult ? false : conditionResult;
          break;
        }

        const { options: customizationOptions } = configuration;
        if (!customizationOptions) {
          conditionResult = conditionResult ? false : conditionResult;
          break;
        }

        const customizationOption = find(
          customizationOptions,
          (option) => option.tag === optionDefinition.optionTag
        );
        if (!customizationOption) {
          conditionResult = conditionResult ? false : conditionResult;
          break;
        }

        conditionResult = conditionResult
          ? customizationOption.value === condition.value
          : conditionResult;

        break;
      }
      default: {
        const exhaustiveCheck: never = optionDefinition.type;
        throw new Error(`Unsupported type "${exhaustiveCheck as string}"`);
      }
    }
  }
  return conditionResult;
}

export function evalContentCondition(
  conditions: ContentCondition[],
  template: Template,
  content: Content
): boolean {
  let conditionResult = true;

  for (const condition of conditions) {
    const { type, operator, variable, value } = condition;

    if (type !== "content") {
      throw new Error(`Unsupported type "${type as string}"`);
    }

    const validOperators = ["equal", "present", "blank"];
    if (!variable || validOperators.includes(operator) === false) {
      conditionResult = false;
      break;
    }

    switch (variable) {
      case "locale":
        if (operator === "equal" && value !== content.locale) {
          conditionResult = false;
        } else if (operator === "present" && !content.locale) {
          conditionResult = false;
        } else if (operator === "blank" && content.locale) {
          conditionResult = false;
        }
        break;
      case "book_front_cover_page_definition_key": {
        if (content.templateType !== "book") {
          conditionResult = false;
          break;
        }
        const frontCoverDefinitionKeys = template.pageDefinitions.reduce(
          (pageKeys: string[], pageDefinition) => {
            if (pageDefinition.type === "front-cover") {
              pageKeys.push(pageDefinition.key);
            }
            return pageKeys;
          },
          []
        );
        const frontCoverPage = content.pages.find((page) =>
          frontCoverDefinitionKeys.includes(page.pageDefinitionKey)
        );
        if (
          operator === "equal" &&
          (!frontCoverPage || frontCoverPage.pageDefinitionKey !== value)
        ) {
          conditionResult = false;
        } else if (operator === "present" && !frontCoverPage) {
          conditionResult = false;
        } else if (
          operator === "blank" &&
          frontCoverPage &&
          (frontCoverPage.editableTexts.length > 0 || frontCoverPage.editablePictures.length > 0)
        ) {
          conditionResult = false;
        }
        break;
      }
      case "book_title":
        if (content.templateType !== "book") {
          conditionResult = false;
        } else if (operator === "equal" && content.bookTitle !== value) {
          conditionResult = false;
        } else if (operator === "present" && !content.bookTitle) {
          conditionResult = false;
        } else if (
          operator === "blank" &&
          !(content.bookTitle === "" || content.bookTitle === undefined)
        ) {
          conditionResult = false;
        }
        break;
      case "book_number":
        if (content.templateType !== "book") {
          conditionResult = false;
        } else if (operator === "equal" && value && content.bookNumber !== +value) {
          conditionResult = false;
        } else if (operator === "present" && !content.bookNumber) {
          conditionResult = false;
        } else if (operator === "blank" && content.bookNumber !== undefined) {
          conditionResult = false;
        }
        break;
      default:
        conditionResult = false;
      // const exhaustiveCheck: never = variable;
      // throw new Error(`Unsupported content condition variable "${exhaustiveCheck}"`);
    }
  }
  return conditionResult;
}

export function firstMatchingCondition<T extends { conditions?: TemplateCondition[] }>(
  items: T[],
  template: Template,
  configuration: Configuration | undefined
): T | undefined {
  return find(items, (item) => {
    if (!item.conditions) {
      return true;
    }

    return evalCondition(item.conditions, template, configuration);
  });
}
